import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "src/Components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormField } from "src/Components/ui/form";
import { Input } from "src/Components/ui/input";
import FormFieldItem from "src/common/form/FormFieldItem";
import { DialogClose } from "src/Components/ui/dialog";
import { Button } from "src/Components/ui/button";
import { toast } from "react-toastify";
import { SelectBoxForm } from "src/Components/selectbox/SelectBoxForm";
import { Textarea } from "src/Components/ui/textarea";
import { Switch } from "src/Components/ui/switch";
import { DatePickerForm } from "src/Components/DatePicker/DatePickerForm";
import UAEDirhamIcon from "src/Components/utils/UAEDirhamIcon";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "src/Components/ui/card";
import {
  Tag,
  Truck,
  Percent,
  DollarSign,
  Calendar,
  ShoppingCart,
  Gift,
  ToggleLeft,
  FileText,
  Sparkles,
  ShoppingBag,
} from "lucide-react";
function CouponForm(props) {
  const { t } = useTranslation();
  const dialogCloseRef = useRef();

  const formSchema = z
    .object({
      code: z.string(),
      type: z
        .union([
          z.string(),
          z.null(),
          z.undefined(),
        ])
        .optional()
        .nullable(),
      value: z
        .union([
          z
            .string()
            .refine((val) => val === "" || !isNaN(Number(val)))
            .transform((val) => (val === "" ? null : Number(val))),
          z.number(),
          z.null(),
          z.undefined(),
        ])
        .optional()
        .nullable(),
      max_uses: z
        .union([
          z
            .string()
            .refine((val) => !isNaN(Number(val)))
            .transform((val) => Number(val)),
          z.number(),
          z.null().optional(),
        ])
        .optional(),
      is_active: z.boolean().optional(),
      is_free_delivery: z.boolean().optional(),
      starts_at: z.union([
        z.date().optional(),
        z.null().optional(),
        z.string().optional(),
        z.string().transform((val) => new Date(val)),
      ]),
      expires_at: z.union([
        z.date().optional(),
        z.null().optional(),
        z.string().optional(),
        z.string().transform((val) => new Date(val)),
      ]),
      minimum_order_amount: z
        .union([
          z
            .string()
            .refine((val) => !isNaN(Number(val)))
            .transform((val) => Number(val)),
          z.null().optional(),
          z.number(),
        ])
        .optional(),
      is_first_register: z.boolean().optional(),
      take_away: z.boolean().optional(),
      description: z.union([
        z.string().optional(),
        z.string().transform((val) => val.trim()),
        z.null().optional(),
      ]),
    })
    .refine(
      (data) => {
        // إذا كان is_free_delivery false أو undefined، يجب أن يكون type و value موجودين
        if (!data.is_free_delivery) {
          const hasType = data.type && data.type !== "";
          const hasValue = data.value !== null && data.value !== undefined && data.value !== "";
          return hasType && hasValue;
        }
        return true; // إذا كان is_free_delivery true، type و value يمكن أن يكونا null
      },
      {
        message: t("errorMessage.fillAll") || "Type and value are required when free delivery is disabled",
        path: ["type"], // يظهر الخطأ على حقل type
      }
    )
    .refine(
      (data) => {
        // التحقق من value أيضاً بشكل منفصل
        if (!data.is_free_delivery) {
          const hasValue = data.value !== null && data.value !== undefined && data.value !== "";
          return hasValue;
        }
        return true;
      },
      {
        message: t("errorMessage.fillAll") || "Value is required when free delivery is disabled",
        path: ["value"], // يظهر الخطأ على حقل value
      }
    );
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...props.datasource,
    },
  });
  const onSubmit = (values) => {
    // معالجة القيم حسب القواعد:
    // 1. إذا كان is_free_delivery = false، يجب أن يكون type و value موجودين (يتم التحقق في validation)
    // 2. إذا كان is_free_delivery = true:
    //    - إذا كان type/value فارغين → أرسل null (توصيل مجاني فقط)
    //    - إذا كان type/value موجودين → أرسل القيم (توصيل مجاني + خصم)
    const processedValues = {
      ...values,
      // معالجة type
      type: values.type && values.type !== "" ? values.type : null,
      // معالجة value: تحويل إلى رقم إذا كان موجوداً، وإلا null
      value: values.value !== "" && values.value !== null && values.value !== undefined
        ? Number(values.value)
        : null,
    };

    props.onSubmit(processedValues);
    if (dialogCloseRef.current) {
      dialogCloseRef.current.click();
    }
  };
  const onErorr = (errors) => {
    toast.error(t("errorMessage.fillAll"));
  };
  const isFreeDelivery = form.watch("is_free_delivery");

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onErorr)}
        className="space-y-6"
      >
        {/* Basic Information Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-primary" />
              {t("gloabl.basic_info") || "Basic Information"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormFieldItem required form={form} label={t("gloabl.code")}>
                  <Input {...field} placeholder={t("gloabl.code") || "Coupon Code"} />
                </FormFieldItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_free_delivery"
              render={({ field }) => (
                <FormFieldItem
                  form={form}
                  label={t("gloabl.is_free_delivery")}
                >
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Truck className="w-5 h-5 text-purple-600" />
                        <span className="font-medium">{t("gloabl.is_free_delivery")}</span>
                      </div>
                      <Switch
                        dir="ltr"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </div>
                    {field.value && (
                      <div className="mt-2 p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
                        <p className="text-xs text-purple-700 dark:text-purple-300 flex items-start gap-2">
                          <Sparkles className="w-3 h-3 mt-0.5 shrink-0" />
                          {t("gloabl.free_delivery_note") || "When enabled, type and value become optional. You can also add a discount along with free delivery."}
                        </p>
                      </div>
                    )}
                  </div>
                </FormFieldItem>
              )}
            />
            <FormField
              control={form.control}
              name="take_away"
              render={({ field }) => (
                <FormFieldItem
                  form={form}
                  label={t("gloabl.is_take_away_only")}
                >
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5 text-emerald-600" />
                        <span className="font-medium">
                          {t("gloabl.is_take_away_only")}
                        </span>
                      </div>
                      <Switch
                        dir="ltr"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </div>
                    {field.value && (
                      <div className="mt-2 p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
                        <p className="text-xs text-emerald-700 dark:text-emerald-300 flex items-start gap-2">
                          <Sparkles className="w-3 h-3 mt-0.5 shrink-0" />
                          {t("gloabl.take_away_only_note") ||
                            "When enabled, this coupon applies only to takeaway orders."}
                        </p>
                      </div>
                    )}
                  </div>
                </FormFieldItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Discount Information Section */}
        <Card className={isFreeDelivery ? "opacity-75" : ""}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {form.watch("type") === "percentage" ? (
                <Percent className="w-5 h-5 text-blue-600" />
              ) : (
                <DollarSign className="w-5 h-5 text-amber-600" />
              )}
              {t("gloabl.discount_info") || "Discount Information"}
            </CardTitle>
            <CardDescription>
              {isFreeDelivery 
                ? t("gloabl.optional_discount") || "Optional: You can add a discount along with free delivery"
                : t("gloabl.required_discount") || "Required: Select discount type and value"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormFieldItem
                    required={!isFreeDelivery}
                    form={form}
                    label={t("gloabl.type")}
                  >
                    <SelectBoxForm
                      textValue
                      data={[
                        { id: "fixed", name: t("gloabl.fixed") },
                        { id: "percentage", name: t("gloabl.percentage") },
                      ]}
                      {...field}
                      placeholder={t("gloabl.select") + " " + t("gloabl.type")}
                      label={"name"}
                      valueKey={"id"}
                    />
                  </FormFieldItem>
                )}
              />
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormFieldItem
                    required={!isFreeDelivery}
                    form={form}
                    label={t("gloabl.value")}
                  >
                    <div className="space-y-2">
                      <div className="relative">
                        {form.watch("type") === "percentage" && (
                          <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        )}
                        {form.watch("type") === "fixed" && (
                          <UAEDirhamIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        )}
                        <Input 
                          type="number" 
                          {...field} 
                          className={form.watch("type") ? "pl-9" : ""}
                          placeholder={form.watch("type") === "percentage" ? "10" : "50"}
                        />
                      </div>
                      {form.watch("type") === "percentage" && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Percent className="w-3 h-3" />
                          {t("gloabl.percentage_note") || "Enter percentage (e.g., 10 = 10%)"}
                        </p>
                      )}
                      {form.watch("type") === "fixed" && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <UAEDirhamIcon className="w-3 h-3" />
                          {t("gloabl.fixed_note") || "Enter fixed amount in AED"}
                        </p>
                      )}
                    </div>
                  </FormFieldItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Usage & Limits Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-green-600" />
              {t("gloabl.usage_limits") || "Usage & Limits"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="max_uses"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={t("gloabl.max_uses")}
                  >
                    <Input 
                      type="number" 
                      {...field} 
                      placeholder={t("gloabl.unlimited") || "Leave empty for unlimited"}
                    />
                  </FormFieldItem>
                )}
              />
              <FormField
                control={form.control}
                name="minimum_order_amount"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={t("gloabl.minimum_order_amount")}
                  >
                    <div className="flex items-center gap-2">
                      <UAEDirhamIcon className="w-4 h-4 text-muted-foreground" />
                      <Input 
                        type="number" 
                        {...field} 
                        placeholder={t("gloabl.no_minimum") || "No minimum"}
                      />
                    </div>
                  </FormFieldItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Validity Period Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-600" />
              {t("gloabl.validity_period") || "Validity Period"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="starts_at"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={t("gloabl.starts_at")}
                  >
                    <DatePickerForm name="starts_at" control={form.control} />
                  </FormFieldItem>
                )}
              />
              <FormField
                control={form.control}
                name="expires_at"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={t("gloabl.expires_at")}
                  >
                    <DatePickerForm name="expires_at" control={form.control} />
                  </FormFieldItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Additional Settings Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ToggleLeft className="w-5 h-5 text-gray-600" />
              {t("gloabl.additional_settings") || "Additional Settings"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={t("gloabl.is_active")}
                  >
                    <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                      <span className="text-sm">{t("gloabl.active") || "Active"}</span>
                      <Switch
                        dir="ltr"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </div>
                  </FormFieldItem>
                )}
              />
              <FormField
                control={form.control}
                name="is_first_register"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={t("gloabl.is_first_register")}
                  >
                    <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{t("gloabl.first_register") || "First Register"}</span>
                      </div>
                      <Switch
                        dir="ltr"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </div>
                  </FormFieldItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Description Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {t("gloabl.description")}
            </CardTitle>
            <CardDescription>
              {t("gloabl.optional_description") || "Optional: Add a description for this coupon"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormFieldItem form={form} label={t("gloabl.description")}>
                  <Textarea 
                    {...field} 
                    placeholder={t("gloabl.description_placeholder") || "Enter coupon description..."}
                    rows={4}
                  />
                </FormFieldItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg">
              {t("gloabl.cancel")}
            </Button>
          </DialogClose>
          <DialogClose ref={dialogCloseRef} />
          <Button type="submit" size="lg">
            {t("gloabl.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default CouponForm;
